import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from './button';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  confirmButtonClass?: string;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  confirmButtonClass
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      // Error handling is the caller's responsibility; just re-enable the button
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (isProcessing) return;
    onClose();
  };

  // Icon and color based on type
  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <XCircle className="w-12 h-12 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-12 h-12 text-amber-600" />;
      case 'info':
        return <Info className="w-12 h-12 text-blue-600" />;
      case 'success':
        return <CheckCircle className="w-12 h-12 text-emerald-600" />;
      default:
        return <AlertTriangle className="w-12 h-12 text-amber-600" />;
    }
  };

  const getHeaderColor = () => {
    switch (type) {
      case 'danger':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-emerald-50 border-emerald-200';
      default:
        return 'bg-amber-50 border-amber-200';
    }
  };

  const defaultConfirmClass = type === 'danger' 
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : type === 'warning'
    ? 'bg-amber-600 hover:bg-amber-700 text-white'
    : 'bg-heritage-blue hover:bg-heritage-blue/90 text-white';

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ isolation: 'isolate', pointerEvents: 'auto' }}
    >
      {/* Backdrop - blocks all interaction with elements behind */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity cursor-pointer"
        onClick={handleCancel}
      />
      
      {/* Dialog */}
      <div 
        className="relative bg-white rounded-lg shadow-2xl max-w-md w-full transform transition-all animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
          {/* Header with Icon */}
          <div className={`flex items-center gap-4 p-6 border-b ${getHeaderColor()}`}>
            {getIcon()}
            <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          </div>
          
          {/* Message */}
          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">
              {message}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="min-w-24"
              disabled={isProcessing}
            >
              {cancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              className={confirmButtonClass || defaultConfirmClass + ' min-w-24'}
              disabled={isProcessing}
            >
              {isProcessing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {confirmText}
            </Button>
          </div>
        </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
